import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UploadSimple, Eye, GraduationCap, Users, DownloadSimple, ArrowSquareOut, FileText } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { PdfViewerModal } from "../components/PdfViewerModal";
import { EvvWindows } from "../components/EvvWindows";
import { SignaturePad } from "../components/SignaturePad";
import ContactOfficeCard from "../components/ContactOfficeCard";
import ReferralCard from "../components/ReferralCard";
import AdoptSignatureCard from "../components/AdoptSignatureCard";
import BadgePhotoEditor from "../components/BadgePhotoEditor";
import { FileGroups } from "../components/EmployeeFileGroups";
import AchievementsChips from "../components/AchievementsChips";
import TempBadgeCard from "../components/TempBadgeCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const SELF_FIELDS = [
  ["name", "Full name", "text"], ["dob", "Date of birth", "date"], ["address", "Address", "text"],
  ["phone", "Phone", "text"], ["phone2", "Alt phone", "text"], ["email", "Email (office updates your login)", "text"],
  ["emergency_contact_name", "Emergency contact name", "text"], ["emergency_contact_phone", "Emergency contact phone", "text"],
];

const DOC_LABELS = { badge_photo: "Badge photo", id_card: "ID card", ss_card: "SS card", credential: "Credential / certification",
  cpr_first_aid: "CPR / First Aid card", auto_insurance: "Auto insurance", tb_test: "TB test", other: "Other document" };

const PHONE_RE = /(\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g;
const PHONE_FULL = /^\+?1?[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

function linkifyPhones(text) {
  return text.split(PHONE_RE).map((part, i) =>
    PHONE_FULL.test(part.trim()) ? (
      <a key={i} href={`tel:${part.replace(/\D/g, "")}`} className="font-medium text-primary hover:underline">{part}</a>
    ) : part
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function LegalRepAttestCard({ me, onSaved }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  if (!me.dedicated_caregiver) return null;
  const stale = !!me.legal_rep_attest_stale;
  const last = (me.legal_rep_attestation || {}).confirmed_at;
  const sign = async () => {
    setBusy(true);
    try {
      await api.post("/profile/me/legal-rep-attest", { signed_name: name });
      toast.success("Attestation signed — thank you!");
      setName("");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className={`mt-6 rounded-xl border p-5 shadow-sm ${stale ? "border-[#4A235A]/40 bg-[#4A235A]/[0.04]" : "border-stone-200 bg-white"}`} data-testid="legal-rep-attest-card">
      <p className="font-heading text-sm font-semibold text-stone-900">Designated caregiver attestation</p>
      <p className="mt-0.5 text-xs leading-relaxed text-stone-500" data-testid="legal-rep-attest-status">
        You care for {me.dedicated_client_name || "one specific client"} as their designated caregiver. Once a year we ask you to confirm in writing that you are not their spouse, Power of Attorney, legal guardian, or representative payee — that you are not eligible to provide paid CHC waiver services to them while any of these roles applies — and that you must notify the office immediately if this information changes, before providing another shift. Nothing about your work changes; this simply documents what has always been the case. A false statement is grounds for termination.
        {last && !stale ? ` Last signed ${String(last).slice(0, 10)} — you're current.` : ""}
        {stale ? " Yours is due now." : ""}
      </p>
      {stale && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full legal name to sign"
            className="max-w-xs border-stone-300 bg-white" data-testid="legal-rep-attest-name" />
          <Button onClick={sign} disabled={busy || name.trim().length < 3}
            className="rounded-full bg-[#4A235A] text-white hover:bg-[#381a45]" data-testid="legal-rep-attest-sign">
            {busy ? "Signing…" : "Sign attestation"}
          </Button>
        </div>
      )}
    </div>
  );
}

function UpdateInfoCard({ me, onSaved }) {
  const p = me.profile || {};
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    phone: p.phone || "", phone2: p.phone2 || "", email: p.email || me.email || "", address: p.address || "",
    emergency_contact_name: p.emergency_contact_name || "", emergency_contact_phone: p.emergency_contact_phone || "",
    availability_days: Array.isArray(p.availability_days) ? p.availability_days.join(", ") : (p.availability_days || ""),
    availability_times: p.availability_times || "", availability_notes: p.availability_notes || "",
  });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const toggleDay = (d) => {
    const cur = f.availability_days ? f.availability_days.split(",").map((x) => x.trim()).filter(Boolean) : [];
    const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d];
    setF((x) => ({ ...x, availability_days: DAYS.filter((dd) => next.includes(dd)).join(", ") }));
  };
  const daysSet = new Set((f.availability_days || "").split(",").map((x) => x.trim()).filter(Boolean));
  const missing = !p.availability_days || !p.emergency_contact_name || !p.phone;
  const stale = !!me.availability_stale;

  const confirmAvailability = async () => {
    setBusy(true);
    try {
      await api.post("/profile/me/confirm-availability");
      toast.success("Availability confirmed — thank you!");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/profile/me", f);
      toast.success("Submitted — an admin will review and confirm");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className={`mt-6 rounded-xl border p-5 shadow-sm ${missing || stale ? "border-primary/40 bg-primary/[0.03]" : "border-stone-200 bg-white"}`} data-testid="update-info-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-heading text-sm font-semibold text-stone-900">Keep your info current</p>
          <p className="mt-0.5 text-xs text-stone-500">
            {stale
              ? "Every 90 days we ask you to confirm your availability — it's how we schedule your shifts. Yours is due: confirm it's still right, or update it."
              : missing
                ? "Please take a minute to update your contact info, emergency contact, and availability — we use these to reach you and to schedule your shifts."
                : "Contact info, emergency contact, and availability — keep them fresh so we can reach you and schedule you well."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {stale && (
            <Button onClick={confirmAvailability} disabled={busy}
              className="rounded-full bg-primary text-primary-foreground" data-testid="availability-confirm-btn">
              {busy ? "Saving…" : "Availability unchanged — confirm"}
            </Button>
          )}
          <Button variant={missing || stale ? "outline" : "outline"} onClick={() => setOpen((o) => !o)}
            className="rounded-full border-stone-300" data-testid="update-info-toggle">
            {open ? "Close" : "Review & update"}
          </Button>
        </div>
      </div>
      {open && (
        <div className="mt-4 space-y-4 border-t border-stone-100 pt-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs">Phone</Label><Input value={f.phone} onChange={set("phone")} className="border-stone-300 bg-white" data-testid="update-phone" /></div>
            <div><Label className="text-xs">Alt phone</Label><Input value={f.phone2} onChange={set("phone2")} className="border-stone-300 bg-white" data-testid="update-phone2" /></div>
            <div><Label className="text-xs">Email</Label><Input value={f.email} onChange={set("email")} className="border-stone-300 bg-white" data-testid="update-email" /></div>
            <div><Label className="text-xs">Address</Label><Input value={f.address} onChange={set("address")} className="border-stone-300 bg-white" data-testid="update-address" /></div>
            <div><Label className="text-xs">Emergency contact name</Label><Input value={f.emergency_contact_name} onChange={set("emergency_contact_name")} className="border-stone-300 bg-white" data-testid="update-ec-name" /></div>
            <div><Label className="text-xs">Emergency contact phone</Label><Input value={f.emergency_contact_phone} onChange={set("emergency_contact_phone")} className="border-stone-300 bg-white" data-testid="update-ec-phone" /></div>
          </div>
          <div className="rounded-lg border border-stone-200 bg-accent/40 p-4" data-testid="availability-section">
            <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Your availability</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`avail-day-${d}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${daysSet.has(d) ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 bg-white text-stone-600 hover:border-primary/50"}`}>
                  {d}
                </button>
              ))}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div><Label className="text-xs">Times you can work</Label><Input value={f.availability_times} onChange={set("availability_times")} placeholder="e.g. mornings, weekends only, anytime" className="border-stone-300 bg-white" data-testid="avail-times" /></div>
              <div><Label className="text-xs">Anything we should know</Label><Input value={f.availability_notes} onChange={set("availability_notes")} placeholder="e.g. no overnights, prefer 4-hour shifts" className="border-stone-300 bg-white" data-testid="avail-notes" /></div>
            </div>
          </div>
          <Button onClick={save} disabled={busy} className="rounded-full bg-primary text-primary-foreground" data-testid="update-info-save">
            {busy ? "Submitting…" : "Submit updates"}
          </Button>
        </div>
      )}
    </div>
  );
}


export default function MyProfilePage() {
  const [me, setMe] = useState(null);
  const [evvSum, setEvvSum] = useState(null);
  const [i9, setI9] = useState(null);
  const [i9Sig, setI9Sig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const [pdfView, setPdfView] = useState(null);
  const [numbers, setNumbers] = useState(null);
  const [offer, setOffer] = useState(null);
  const [offerSig, setOfferSig] = useState(null);
  const [myTraining, setMyTraining] = useState(null);
  const [evvLetters, setEvvLetters] = useState([]);
  const [evvLetterName, setEvvLetterName] = useState({});
  const [evvLetterSig, setEvvLetterSig] = useState({});
  const [badgeEditorOpen, setBadgeEditorOpen] = useState(false);
  const [myFile, setMyFile] = useState(null);

  const load = () => api.get("/profile/me").then((r) => {
    setMe(r.data);
    const p = r.data.profile || {};
    setForm(Object.fromEntries(SELF_FIELDS.map(([k]) => [k, k === "name" ? (r.data.name || "") : (p[k] || "")])));
  }).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (me?.id) api.get(`/users/${me.id}/evv-summary`).then((r) => setEvvSum(r.data)).catch(() => {}); }, [me?.id]);
  useEffect(() => { if (me?.id) api.get(`/employees/${me.id}/file`).then((r) => setMyFile(r.data)).catch(() => {}); }, [me?.id]);
  const [evvLadder, setEvvLadder] = useState(null);
  useEffect(() => { api.get("/evv/ladder/mine").then((r) => setEvvLadder(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (me?.id) api.get(`/users/${me.id}/i9`).then((r) => setI9(r.data.i9?.status ? r.data.i9 : null)).catch(() => {}); }, [me?.id]);
  useEffect(() => {
    api.get("/orientation").then((r) => setNumbers((r.data.steps || []).find((s) => s.title === "Important Numbers & Agencies") || null)).catch(() => {});
  }, []);
  useEffect(() => { if (me?.id) api.get(`/users/${me.id}/offer`).then((r) => r.data.exists && setOffer(r.data.offer)).catch(() => {}); }, [me?.id]);
  useEffect(() => { if (me?.id) api.get("/my/training-status").then((r) => setMyTraining(r.data.topics || [])).catch(() => {}); }, [me?.id]);
  useEffect(() => { if (me?.id) api.get("/evv/letters/mine").then((r) => setEvvLetters(r.data.letters || [])).catch(() => {}); }, [me?.id]);

  const signEvvLetter = async (l) => {
    const typed = (evvLetterName[l.id] || "").trim();
    if (!typed) { toast.error("Type your full name to sign"); return; }
    setBusy(true);
    try {
      await api.post(`/evv/letters/${l.id}/sign`, { typed_name: typed, signature_png: evvLetterSig[l.id] || "" });
      toast.success("Acknowledgement signed — thank you");
      const r = await api.get("/evv/letters/mine");
      setEvvLetters(r.data.letters || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const signOffer = async () => {
    if (!offerSig) { toast.error("Please draw your signature"); return; }
    try {
      await api.post(`/users/${me.id}/offer/sign`, { signature_png: offerSig });
      toast.success("Offer accepted and signed — welcome aboard!");
      const r = await api.get(`/users/${me.id}/offer`);
      setOffer(r.data.offer);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const signI9 = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${me.id}/i9/employee-sign`, { signature_png: i9Sig });
      toast.success("I-9 signed — the office will countersign");
      const r = await api.get(`/users/${me.id}/i9`);
      setI9(r.data.i9);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const openCert = async (cid) => {
    try {
      const res = await api.get(`/training/completions/${cid}/certificate`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!me || !form) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const p = me.profile || {};
  const pending = me.pending_profile;

  const submit = async () => {
    setBusy(true);
    try {
      await api.put("/profile/me", form);
      toast.success("Changes submitted — they'll appear after an admin approves them");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const uploadDoc = async (key, f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/users/${me.id}/documents/${key}`, fd);
      toast.success(`${DOC_LABELS[key] || "Document"} uploaded`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl" data-testid="my-profile-page">
      <Link to="/dashboard" className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-stone-500 hover:text-primary" data-testid="back-to-dashboard">
        ← Back to dashboard
      </Link>
      <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">My Profile</h1>
      <p className="mt-1 text-sm text-stone-500">Your employee record. Edits you make are reviewed by an admin before they take effect.</p>

      {pending && (
        <div className="mt-6 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 p-4" data-testid="profile-pending-banner">
          <p className="text-sm font-semibold text-[#8a5d14]">Changes pending admin approval:</p>
          <ul className="mt-1 list-inside list-disc text-xs text-stone-600">
            {Object.entries(pending).map(([k, v]) => <li key={k}>{k.replace(/_/g, " ")}: {v}</li>)}
          </ul>
        </div>
      )}

      <div className="mt-6">
        <ContactOfficeCard />
      </div>

      <UpdateInfoCard me={me} onSaved={load} />
      <LegalRepAttestCard me={me} onSaved={load} />
      <AdoptSignatureCard />
      <ReferralCard />

      {offer && (
        <div className="mt-6 rounded-xl border-2 border-primary/30 bg-white p-5 shadow-sm" data-testid="my-offer-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="font-heading text-sm font-semibold text-stone-900">Offer of Employment</p>
            {offer.signed ? (
              <span className="rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-3 py-1 text-xs font-medium text-[#1e8449]" data-testid="my-offer-signed">
                ✓ Signed {String(offer.signed.signed_at).slice(0, 10)}
              </span>
            ) : (
              <span className="rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-3 py-1 text-xs font-medium text-[#8a5d14]" data-testid="my-offer-pending">Review & sign to accept</span>
            )}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4" data-testid="my-offer-details">
            <div><p className="text-xs text-stone-400">Position</p><p className="font-medium text-stone-800">{offer.position || "Direct Care Worker"}</p></div>
            <div><p className="text-xs text-stone-400">Pay rate</p><p className="font-medium text-stone-800">${offer.pay_rate || "—"}/hr</p></div>
            <div><p className="text-xs text-stone-400">Start date</p><p className="font-medium text-stone-800">{offer.start_date || "—"}</p></div>
            <div><p className="text-xs text-stone-400">Schedule</p><p className="font-medium text-stone-800">{offer.schedule_notes || "—"}</p></div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button onClick={async () => {
                try {
                  const res = await api.get(`/users/${me.id}/offer/pdf`, { responseType: "blob" });
                  window.open(URL.createObjectURL(new Blob([res.data], { type: "application/pdf" })), "_blank");
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="text-sm font-medium text-primary hover:underline" data-testid="my-offer-view-pdf">
              View the full offer letter (PDF)
            </button>
          </div>
          {!offer.signed && (
            <div className="mt-4 space-y-3 border-t border-stone-100 pt-4">
              <p className="text-xs text-stone-500">By signing you accept this offer of employment (at-will) as described in the letter.</p>
              <SignaturePad onChange={setOfferSig} />
              <div className="flex justify-end">
                <Button onClick={signOffer} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="my-offer-sign">
                  Accept & sign offer
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="profile-card">
        <div className="flex flex-wrap items-start gap-6">
          <div className="flex flex-col items-center gap-2">
            {me.documents?.badge_photo ? (
              <img src={`${api.defaults.baseURL}/users/${me.id}/documents/badge_photo`} alt="Badge"
                className="h-28 w-28 rounded-lg border border-stone-200 object-cover" data-testid="badge-photo" />
            ) : (
              <div className="flex h-28 w-28 items-center justify-center rounded-lg border border-dashed border-stone-300 text-xs text-stone-400" data-testid="badge-photo-empty">No photo</div>
            )}
            <button onClick={() => setBadgeEditorOpen(true)} className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-stone-300 px-3 text-xs font-medium text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="badge-upload-button">
              <UploadSimple size={14} /> {me.documents?.badge_photo ? "Replace" : "Upload"} photo
            </button>
            <BadgePhotoEditor uid={me.id} open={badgeEditorOpen} onClose={() => setBadgeEditorOpen(false)} onSaved={load} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-lg font-semibold text-stone-900" data-testid="profile-name">{me.name || "—"}</p>
            <p className="text-sm text-stone-500">{me.email} · {me.role === "staff" ? "DCW" : me.role}</p>
            <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              <p className="text-stone-600"><span className="text-stone-400">Employee ID:</span> {p.employee_id || "—"}</p>
              <p className="text-stone-600"><span className="text-stone-400">Start date:</span> {p.start_date || "—"}</p>
              <p className="text-stone-600"><span className="text-stone-400">DOB:</span> {p.dob || "—"}</p>
              <p className="text-stone-600"><span className="text-stone-400">Phone:</span> {p.phone || "—"}</p>
              <p className="col-span-2 text-stone-600"><span className="text-stone-400">Address:</span> {p.address || "—"}</p>
            </div>
            <Button variant="outline" onClick={() => setEditing(!editing)} className="mt-4 border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white" data-testid="profile-edit-toggle">
              {editing ? "Cancel" : "Update my information"}
            </Button>
            <AchievementsChips uid={me.id} />
          </div>
        </div>

        {editing && (
          <div className="mt-5 border-t border-stone-100 pt-5" data-testid="profile-edit-form">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {SELF_FIELDS.map(([k, label, type]) => (
                <div key={k} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Input type={type || "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="border-stone-300 bg-white" data-testid={`profile-edit-${k}`} />
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-stone-400">Employee ID and start date can only be changed by the office.</p>
            <label className="mt-3 flex items-center gap-2 text-sm text-stone-600" data-testid="profile-sms-optin-label">
              <input type="checkbox" checked={form.sms_opt_in === "yes"} onChange={(e) => setForm({ ...form, sms_opt_in: e.target.checked ? "yes" : "" })} data-testid="profile-sms-optin" />
              Yes, the office may text me about my schedule and trainings
            </label>
            <div className="mt-4 flex justify-end">
              <Button onClick={submit} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="profile-edit-submit">
                {busy ? "Submitting..." : "Submit for approval"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {me?.id && <TempBadgeCard uid={me.id} />}

      {evvLadder && evvLadder.step > 0 && (
        <div className={`mt-6 rounded-xl border-2 p-5 shadow-sm ${evvLadder.step >= 2 ? "border-[#C0392B]/40 bg-[#C0392B]/5" : "border-[#B7791F]/40 bg-[#B7791F]/5"}`} data-testid="my-evv-ladder">
          <p className="font-heading text-sm font-semibold text-stone-900">EVV compliance — {evvLadder.step_label}</p>
          <p className="mt-1 text-xs text-stone-600">
            {evvLadder.step === 1 && evvLadder.needs_policy_sign && <>Your visit verification has been under {evvLadder.threshold}% for {evvLadder.consecutive_below} weeks in a row. Please review and sign the <strong>{evvLadder.policy_title || "EVV policy"}</strong> on the Policies page this week — then a week back at {evvLadder.threshold}%+ clears this.</>}
            {evvLadder.step === 1 && !evvLadder.needs_policy_sign && "You've signed the policy review — this clears automatically once your weekly number is back at 90%+."}
            {evvLadder.step === 2 && "The office will schedule a meeting with you about visit verification. If anything makes clocking in hard, call 267-547-0310 — we can fix visits in real time when you call."}
            {evvLadder.step === 3 && "Your schedule is now fixed by the office, with a reminder before every shift and a same-day office alert if a shift starts without a clock-in. This is the last step in the compliance process — call 267-547-0310 if anything makes clocking in hard."}
          </p>
          {evvLadder.needs_policy_sign && (
            <Link to="/policies">
              <Button size="sm" className="mt-3 rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="my-evv-ladder-sign">Review & sign the policy</Button>
            </Link>
          )}
        </div>
      )}

      {evvSum && (evvSum.overall?.months > 0 || evvSum.previous_week?.total || evvSum.last_30_days?.total) && (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="my-evv-card">
          <p className="font-heading text-sm font-semibold text-stone-900">My EVV compliance</p>
          <p className="mt-0.5 text-xs text-stone-400">Clock in and out for every visit — it keeps these numbers up.</p>
          <div className="mt-3"><EvvWindows data={evvSum} testPrefix="my-evv-w" /></div>
        </div>
      )}

      {evvLetters.length > 0 && (
        <div className="mt-6 space-y-4" data-testid="my-evv-letters">
          {evvLetters.map((l) => (
            <div key={l.id} className={`rounded-xl border-2 bg-white p-6 shadow-sm ${l.status === "approved" ? "border-primary/30" : "border-[#27AE60]/30"}`} data-testid={`my-evv-letter-${l.id}`}>
              <p className="font-heading text-sm font-semibold text-stone-900">EVV compliance acknowledgement</p>
              <p className="mt-0.5 text-xs text-stone-400">From the office · {String(l.created_at || "").slice(0, 10)}</p>
              <pre className="mt-3 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md bg-stone-50 p-3 text-xs text-stone-700" data-testid={`my-evv-letter-body-${l.id}`}>{l.body}</pre>
              {l.status === "approved" ? (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-stone-500">Type your full legal name to sign *</label>
                    <input value={evvLetterName[l.id] || ""} onChange={(e) => setEvvLetterName((cur) => ({ ...cur, [l.id]: e.target.value }))}
                      className="h-9 w-full max-w-sm rounded-md border border-stone-300 bg-white px-3 text-sm" data-testid={`my-evv-letter-name-${l.id}`} />
                  </div>
                  <div>
                    <p className="mb-1 text-xs font-medium text-stone-500">Signature (optional but recommended)</p>
                    <SignaturePad onChange={(png) => setEvvLetterSig((cur) => ({ ...cur, [l.id]: png }))} />
                  </div>
                  <Button onClick={() => signEvvLetter(l)} disabled={busy || !(evvLetterName[l.id] || "").trim()}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`my-evv-letter-sign-${l.id}`}>
                    Sign acknowledgement
                  </Button>
                </div>
              ) : (
                <p className="mt-3 text-sm font-semibold text-[#1e8449]" data-testid={`my-evv-letter-signed-${l.id}`}>
                  Signed ✓ {l.signed_name} · {String(l.signed_at || "").slice(0, 10)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {i9 && !i9.employee_signed && (
        <div className="mt-6 rounded-xl border-2 border-primary/30 bg-white p-6 shadow-sm" data-testid="i9-card">
          <p className="font-heading text-sm font-semibold text-stone-900">Your I-9 is ready — review & sign</p>
          <p className="mt-1 text-xs text-stone-500">Section 1 was pre-filled at your interview: {i9.section1?.first_name} {i9.section1?.last_name} · DOB {i9.section1?.dob || "—"} · {i9.section1?.address || "no address"}</p>
          <p className="mt-1 text-xs text-stone-500">By signing you attest, under penalty of perjury, that the information is true and that you are authorized to work in the United States.</p>
          <div className="mt-3"><SignaturePad onChange={setI9Sig} /></div>
          <Button onClick={signI9} disabled={busy || !i9Sig} className="mt-3 rounded-full bg-primary text-primary-foreground hover:bg-stone-900" data-testid="i9-sign-button">
            Sign Section 1
          </Button>
        </div>
      )}
      {i9 && i9.employee_signed && (
        <div className="mt-6 rounded-xl border border-[#27AE60]/30 bg-[#27AE60]/5 p-5 shadow-sm" data-testid="i9-done-card">
          <p className="text-sm font-semibold text-[#1e8449]">
            I-9 signed ✓ {i9.employer_signed ? "· countersigned by the office — complete" : "· waiting for office countersignature"}
          </p>
        </div>
      )}

      <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="profile-documents">
        <p className="font-heading text-sm font-semibold text-stone-900">Documents on file</p>
        <p className="mt-0.5 text-xs text-stone-400">Take a photo or choose a file, pick what it is, and upload — the office sees it on your record right away.</p>
        <div className="mt-3 space-y-2">
          {Object.entries(DOC_LABELS).map(([key, label]) => (
            <div key={key} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`profile-doc-${key}`}>
              <span className="text-stone-700">{label}</span>
              <span className="flex items-center gap-2">
                {me.documents?.[key] ? (
                  <button type="button" onClick={() => setPdfView({ url: `${api.defaults.baseURL}/users/${me.id}/documents/${key}`, title: label })}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`profile-doc-view-${key}`}>
                    <Eye size={13} /> {me.documents[key].filename} ({String(me.documents[key].uploaded_at).slice(0, 10)})
                  </button>
                ) : (
                  <span className="text-xs text-stone-400">Not on file</span>
                )}
                <label className="inline-flex h-7 cursor-pointer items-center gap-1 rounded-md border border-stone-300 px-2 text-xs font-medium text-stone-700 hover:bg-stone-900 hover:text-white" data-testid={`profile-doc-upload-${key}`}>
                  <UploadSimple size={12} /> {busy ? "Uploading…" : me.documents?.[key] ? "Replace" : "Upload"}
                  <input type="file" accept=".pdf,image/*" className="hidden" data-testid={`profile-doc-input-${key}`}
                    onChange={(e) => { uploadDoc(key, e.target.files?.[0]); e.target.value = ""; }} />
                </label>
              </span>
            </div>
          ))}
        </div>
      </div>
      <PdfViewerModal url={pdfView?.url} title={pdfView?.title || ""} onClose={() => setPdfView(null)} />

      {myFile && (
        <div className="mt-8" data-testid="profile-file-groups">
          <p className="font-heading text-lg font-semibold text-stone-900">My employee file</p>
          <p className="mb-4 mt-0.5 text-xs text-stone-400">Everything on your record — the same view the office sees. Tap any document to open it.</p>
          <div className="space-y-6">
            <FileGroups file={myFile} emp={me} canUpload={false}
              onOpenDoc={(d) => window.open(`${api.defaults.baseURL}/employee-file-docs/${d.id}/file`, "_blank")} />
          </div>
        </div>
      )}

      {myTraining && myTraining.length > 0 && (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="profile-my-trainings">
          <p className="flex items-center gap-1.5 font-heading text-sm font-semibold text-stone-900"><GraduationCap size={16} /> My trainings &amp; renewals</p>
          <p className="mt-0.5 text-xs text-stone-400">Every training the agency tracks, with your last completion and next due date.</p>
          <div className="mt-3 space-y-1.5">
            {myTraining.map((t) => (
              <div key={t.topic_id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`my-training-${t.topic_id}`}>
                <span className="text-stone-700">{t.title}</span>
                <span className="flex items-center gap-3 text-xs">
                  <span className="text-stone-500">{t.last_done ? `done ${t.last_done}` : "no record"}</span>
                  {t.renew_by && <span className="text-stone-500">due {t.renew_by}</span>}
                  <span className={`rounded-full border px-2.5 py-0.5 font-medium ${
                    t.status === "current" ? "border-[#27AE60]/30 bg-[#27AE60]/10 text-[#1e8449]"
                    : t.status === "due_soon" ? "border-[#B7791F]/30 bg-[#B7791F]/10 text-[#8a5d14]"
                    : t.status === "overdue" ? "border-[#C0392B]/30 bg-[#C0392B]/10 text-[#C0392B]"
                    : "border-stone-300 bg-stone-100 text-stone-500"}`} data-testid={`my-training-status-${t.topic_id}`}>
                    {t.status === "current" ? "Current" : t.status === "due_soon" ? "Due soon" : t.status === "overdue" ? "Overdue" : "Not done yet"}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="profile-training">
        <p className="flex items-center gap-1.5 font-heading text-sm font-semibold text-stone-900"><GraduationCap size={16} /> Training & guideline sign-offs</p>
        <div className="mt-3 space-y-1.5">
          {me.training.length === 0 && <p className="text-xs text-stone-400">No agency guidelines published yet.</p>}
          {me.training.map((t) => (
            <div key={t.id} className="flex items-center justify-between text-sm" data-testid={`profile-training-${t.id}`}>
              <span className="text-stone-700">{t.title}</span>
              {t.current ? (
                <span className="rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2.5 py-0.5 text-xs font-medium text-[#1e8449]">Signed {String(t.signed_at).slice(0, 10)}</span>
              ) : t.signed_version ? (
                <span className="rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-2.5 py-0.5 text-xs font-medium text-[#8a5d14]">Re-review needed</span>
              ) : (
                <span className="rounded-full border border-[#C0392B]/30 bg-[#C0392B]/10 px-2.5 py-0.5 text-xs font-medium text-[#C0392B]">Not signed</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {(me.courses || []).length > 0 && (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="profile-courses">
          <p className="flex items-center gap-1.5 font-heading text-sm font-semibold text-stone-900"><GraduationCap size={16} /> Training courses</p>
          <div className="mt-3 space-y-1.5">
            {me.courses.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`profile-course-${c.id}`}>
                <span className="text-stone-700">{c.title}{c.cadence_months > 0 && <span className="text-xs text-stone-400"> · every {c.cadence_months} mo</span>}</span>
                <span className="flex items-center gap-3">
                  {c.applicable === false ? (
                    <span className="text-xs italic text-stone-400">Only applies with matching client tasks ({(c.task_codes || []).join(", ")})</span>
                  ) : c.state === "current" ? (
                    <span className="rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2.5 py-0.5 text-xs font-medium text-[#1e8449]">Current — completed {c.passed_at}{c.mode === "graded" ? ` (${c.score}%)` : ""}</span>
                  ) : c.state === "due_soon" ? (
                    <span className="rounded-full border border-[#B7791F]/30 bg-[#B7791F]/10 px-2.5 py-0.5 text-xs font-medium text-[#8a5d14]">Renew by {c.due_date}</span>
                  ) : c.state === "renewal_overdue" ? (
                    <span className="rounded-full border border-[#C0392B]/30 bg-[#C0392B]/10 px-2.5 py-0.5 text-xs font-medium text-[#C0392B]">Overdue — was due {c.due_date}</span>
                  ) : c.state === "initial_ok" ? (
                    <span className="rounded-full bg-stone-100 px-2.5 py-0.5 text-xs font-medium text-stone-500">Due by {c.due_date}</span>
                  ) : (
                    <span className="rounded-full border border-[#C0392B]/30 bg-[#C0392B]/10 px-2.5 py-0.5 text-xs font-medium text-[#C0392B]">Not completed{c.due_date ? ` — was due ${c.due_date}` : ""}</span>
                  )}
                  {(c.completion_id || c.passed) && (
                    <button onClick={() => c.completion_id
                      ? openCert(c.completion_id)
                      : window.open(`${api.defaults.baseURL}/users/${me.id}/courses/${c.id}/certificate`, "_blank")}
                      className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`profile-cert-${c.id}`}>
                      <DownloadSimple size={13} /> Certificate
                    </button>
                  )}
                  <a href={`/train/${c.token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`profile-course-open-${c.id}`}>
                    <ArrowSquareOut size={13} /> {c.passed && !c.overdue ? "Review" : "Take training"}
                  </a>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(me.screenings || []).length > 0 && (
        <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="profile-screenings">
          <p className="flex items-center gap-1.5 font-heading text-sm font-semibold text-stone-900"><FileText size={16} /> Screening forms</p>
          <div className="mt-3 space-y-1.5">
            {me.screenings.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`profile-screening-${s.id}`}>
                <span className="text-stone-700">{s.title}{s.cadence_months > 0 && <span className="text-xs text-stone-400"> · every {s.cadence_months} mo</span>}</span>
                <span className="flex items-center gap-3">
                  {s.signed_at ? (
                    <span className="rounded-full border border-[#27AE60]/30 bg-[#27AE60]/10 px-2.5 py-0.5 text-xs font-medium text-[#1e8449]">Completed {s.signed_at}</span>
                  ) : (
                    <span className="rounded-full border border-[#C0392B]/30 bg-[#C0392B]/10 px-2.5 py-0.5 text-xs font-medium text-[#C0392B]">Not completed</span>
                  )}
                  <a href={`/screen/${s.token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid={`profile-screening-open-${s.id}`}>
                    <ArrowSquareOut size={13} /> Open form
                  </a>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="profile-assignments">
        <p className="flex items-center gap-1.5 font-heading text-sm font-semibold text-stone-900"><Users size={16} /> Clients assigned to me</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(me.assigned_clients || []).length === 0 && <p className="text-xs text-stone-400">No clients assigned yet — ask the office.</p>}
          {(me.assigned_clients || []).map((c) => (
            <span key={c.id} className="rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground" data-testid={`profile-client-${c.id}`}>{c.client_code}</span>
          ))}
        </div>
      </div>
      {numbers && (
        <div className="mt-6 rounded-xl border-2 border-primary/30 bg-white p-5 shadow-sm" data-testid="important-numbers-card">
          <p className="font-heading text-sm font-semibold text-stone-900">Important numbers &amp; agencies</p>
          <p className="mt-0.5 text-xs text-stone-500">Running late, calling out, or an emergency? Call — keep calling until you reach someone. A text isn't received until it's confirmed.</p>
          <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-stone-700" data-testid="important-numbers-content">
            {linkifyPhones(numbers.content)}
          </div>
          <Link to="/quick-reference" className="mt-3 inline-block text-sm font-medium text-primary hover:underline" data-testid="important-numbers-quick-ref-link">
            View the caregiver quick reference sheet →
          </Link>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="likeness-link-card">
        <p className="font-heading text-sm font-semibold text-stone-900">Photos of you on our website</p>
        <p className="mt-0.5 text-xs text-stone-500">We'd love to feature you. Review each photo, approve or decline it, and add a short bio if you'd like one.</p>
        <Link to="/likeness" className="mt-3 inline-block text-sm font-medium text-primary hover:underline" data-testid="likeness-link">
          Review my website photos →
        </Link>
      </div>


    </div>
  );
}
